import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import HelicopterFlightModel from './HelicopterFlightModel.js';
import HelicopterAIPilot from './HelicopterAIPilot.js';
import HitVolume from '../weapons/HitVolume.js';
import { ENEMIES, FLARES } from '../core/SimConfig.js';
import { rand, srand } from '../utils.js';

const _emitterWorld = new THREE.Vector3();
const _bodyRight = new THREE.Vector3();

/**
 * One AI-controlled helicopter: a cloned GLB model + kinematic flight model
 * + AI pilot. The model's local +Z forward is matched to the player Aircraft
 * convention (forward = -Z at zero heading) by setting heading directly on
 * the flight model.
 */
export default class EnemyHelicopter {
  constructor({ model, params, position, heading, scene, particleSystem, explosionSystem = null, audioManager = null, flareSystem = null, initialHealth = 100 }) {
    this.params = params;
    this.particleSystem = particleSystem;
    this.explosionSystem = explosionSystem;
    this.audioManager = audioManager;
    this.flareSystem = flareSystem;
    this.health = initialHealth;
    this.dead = false;          // true once below terrain — EnemyManager prunes
    this.paused = true;         // dormant until EnemyManager clears the takeoff grace
    this.spinDirection = 0;     // -1 / +1 once a runaway yaw-spin is triggered

    // Audio tag — feeds the shared rotor loop keyed by AUDIO.enemyLoops
    // (AudioManager._updateEnemyLoops), summed with every other live helicopter.
    this.audioLoop = 'helicopter';

    // RWR tagging — helicopters carry surveillance radar but no missiles.
    this.radarEquipped = true;
    this.missileEquipped = false;

    // Countermeasure burst state — Mi-8 has no MAWS. Bursts are triggered
    // when the pilot loses its formation leader (detected in update()).
    this._flareReactionTimer = 0;
    this._flareBurstRemaining = 0;
    this._flareBurstTimer = 0;
    this._lastLeader = null;

    // Clone of the GLB so multiple enemies can share the source asset.
    // SkeletonUtils.clone is a superset of Object3D.clone — handles rigged
    // meshes correctly too, in case future helicopters have rotor bones.
    this.modelRoot = cloneSkeleton(model);

    // Enable shadows on every mesh in the clone
    this.modelRoot.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    this.group = new THREE.Group();
    this.group.add(this.modelRoot);
    scene.add(this.group);

    this.flight = new HelicopterFlightModel(params);
    this.flight.position.copy(position);
    this.flight.heading = heading;

    this.pilot = new HelicopterAIPilot(params);

    // Resolve rotor meshes once (cheap per-frame access)
    this.rotors = [];
    if (params.rotors) {
      for (const cfg of Object.values(params.rotors)) {
        const mesh = this.modelRoot.getObjectByName(cfg.mesh);
        if (mesh) this.rotors.push({ mesh, axis: cfg.axis });
        else console.warn(`EnemyHelicopter: rotor mesh "${cfg.mesh}" not found`);
      }
    }

    // Collision volume — used by WeaponSystem and future projectile types.
    const hitboxNode = this.modelRoot.getObjectByName('Hitbox');
    if (hitboxNode) {
      // Hide the hitbox geometry if it's a Mesh — it's a collision proxy, not visual.
      if (hitboxNode.isMesh) hitboxNode.visible = false;
      this.hitVolume = new HitVolume(hitboxNode);
    } else {
      this.hitVolume = null;
      console.warn('EnemyHelicopter: "Hitbox" node not found in model');
    }

    // Damage smoke emitter anchor on the model.
    this.emitterNode = this.modelRoot.getObjectByName('Emitter');
    if (!this.emitterNode) {
      console.warn('EnemyHelicopter: "Emitter" node not found in model');
    }
  }

  update(dt, terrainCollider) {
    // Dormant during the takeoff grace: hold the spawn pose. Don't integrate the
    // flight model or the heli would drop out of its hover; keep the hit volume
    // live so it stays targetable.
    if (this.paused) {
      this.group.position.copy(this.flight.position);
      this.group.quaternion.copy(this.flight.quaternion);
      if (this.hitVolume) this.hitVolume.update();
      return;
    }

    this._applyDamageEffects(dt);
    this._updateFlareBurst(dt);

    // Skip AI inputs once structurally failed — the flight model integrates
    // gravity + tumble on its own.
    if (!this.flight.structuralFailure) {
      this.pilot.update(dt, this.flight, terrainCollider);
      this._detectLeaderLoss();
    } else {
      // Ongoing chaos torque on top of initial spin — keeps the tumble lively.
      const j = ENEMIES.damage.failureAngularJitter;
      this.flight.angularVelocity.x += srand() * j * dt;
      this.flight.angularVelocity.y += srand() * j * dt;
      this.flight.angularVelocity.z += srand() * j * dt;
    }
    this.flight.update(dt);
    this.group.position.copy(this.flight.position);
    this.group.quaternion.copy(this.flight.quaternion);

    // Despawn once the wreck has sunk below the terrain.
    if (terrainCollider) {
      const ground = terrainCollider.getHeightAt(this.flight.position.x, this.flight.position.z);
      if (ground != null && this.flight.position.y < ground) {
        if (this.particleSystem) {
          this.particleSystem.spawnExplosion(
            this.flight.position.x, ground, this.flight.position.z,
            { size: 2 });
        }
        // Filmed fireball on top of the particle blast — the impact is on the
        // deck, which is the one place the ground-anchored billboard reads
        // right (SimConfig.EXPLOSION_SPRITES). `ground`, not position.y: the
        // wreck is already a little under the terrain when this trips.
        this.explosionSystem?.spawnEvent('aircraftGroundImpact',
          this.flight.position.x, ground, this.flight.position.z);
        if (this.audioManager) {
          this.audioManager.playExplosion(
            'Explosion_Crash',
            this.flight.position.x, ground, this.flight.position.z,
          );
        }
        // EnemyManager turns this into a permanent crash-site fire when it
        // prunes the wreck (fx/GroundFireSystem).
        this.crashSite = { x: this.flight.position.x, y: ground, z: this.flight.position.z };
        this.dead = true;
        return;
      }
    }

    // Rotors spin much faster than any reasonable frame rate can sample, so
    // pick a random angle each frame — this gives a continuous blur and
    // avoids stroboscopic stutter from frame/speed aliasing.
    for (const r of this.rotors) {
      r.mesh.rotation[r.axis] = Math.random() * Math.PI * 2;
    }

    if (this.hitVolume) this.hitVolume.update();

    // Damage smoke trails — exclusive stages, lowest threshold wins.
    if (this.particleSystem && this.emitterNode) {
      this.emitterNode.getWorldPosition(_emitterWorld);
      const v = this.flight.velocity;
      if (this.health < 40) {
        this.particleSystem.spawnFireSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
      } else if (this.health < 60) {
        this.particleSystem.spawnBlackSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
      } else if (this.health < 80) {
        this.particleSystem.spawnWhiteSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
      }
    }
  }

  /** Apply damage from a projectile hit. */
  onProjectileHit(_x, _y, _z, damage = 0, _source = null) {
    this.health = Math.max(0, this.health - damage);
  }

  // ── Countermeasures ──────────────────────────────────────────────────────

  // Schedule a panic burst. No-op if a burst is already in progress so a chain
  // of leader losses (or repeated triggers) doesn't reset the cadence.
  _beginFlareBurst() {
    if (this._flareReactionTimer > 0 || this._flareBurstRemaining > 0) return;
    const ai = FLARES.ai;
    this._flareReactionTimer = rand(ai.reactionDelay[0], ai.reactionDelay[1]);
    this._flareBurstRemaining = Math.floor(rand(ai.burstCount[0], ai.burstCount[1] + 1));
    this._flareBurstTimer = 0;
  }

  // Trigger when pilot.leader transitions from set to null this frame —
  // the helicopter pilot promotes to leader on its leader's death/structural
  // failure (HelicopterAIPilot.update), so an unset leader after a tick that
  // had one is the loss event. Only the immediate follower of a fallen leader
  // sees this transition, so chains don't cascade-fire flares.
  _detectLeaderLoss() {
    const currentLeader = this.pilot.leader;
    if (this._lastLeader && !currentLeader) this._beginFlareBurst();
    this._lastLeader = currentLeader;
  }

  _updateFlareBurst(dt) {
    if (this._flareReactionTimer > 0) {
      this._flareReactionTimer -= dt;
      if (this._flareReactionTimer > 0) return;
    }
    if (this._flareBurstRemaining <= 0) return;
    this._flareBurstTimer -= dt;
    if (this._flareBurstTimer > 0) return;

    this._dispenseFlare();
    this._flareBurstRemaining--;
    this._flareBurstTimer = FLARES.dispenseInterval;
  }

  // Two flares ejected sideways in opposite directions (along ±body-right)
  // from the model origin, carrying the helicopter's inertial velocity plus
  // the eject impulse.
  _dispenseFlare() {
    if (!this.flareSystem) return;
    _bodyRight.set(1, 0, 0).applyQuaternion(this.flight.quaternion);
    const speed = FLARES.ejectSpeed;
    const px = this.flight.position.x;
    const py = this.flight.position.y;
    const pz = this.flight.position.z;
    const v = this.flight.velocity;
    this.flareSystem.dispense(
      px, py, pz,
      v.x + _bodyRight.x * speed,
      v.y + _bodyRight.y * speed,
      v.z + _bodyRight.z * speed,
    );
    this.flareSystem.dispense(
      px, py, pz,
      v.x - _bodyRight.x * speed,
      v.y - _bodyRight.y * speed,
      v.z - _bodyRight.z * speed,
    );
  }

  /**
   * Update health-driven flight degradation. Sets liftScale, triggers the
   * structural-failure mode, and bleeds health down once below the decay
   * threshold.
   */
  _applyDamageEffects(dt) {
    const d = ENEMIES.damage;

    // 1. Self-decay below the threshold (irreversible spiral toward 0).
    if (this.health < d.decayThreshold && this.health > 0) {
      this.health = Math.max(0, this.health - d.decayRate * dt);
    }

    // 2. Lift loss — scale linearly from 1 at liftLossThreshold down to
    //    liftLossFloor at health 0.
    if (this.health < d.liftLossThreshold) {
      const t = this.health / d.liftLossThreshold; // 0..1
      this.flight.liftScale = d.liftLossFloor + (1 - d.liftLossFloor) * t;
    } else {
      this.flight.liftScale = 1;
    }

    // 3. Structural failure — latched once tripped.
    if (!this.flight.structuralFailure && this.health < d.structuralFailureThreshold) {
      this.flight.structuralFailure = true;
      this.flight.failureGravity = d.failureGravity;
      const k = d.failureAngularImpulse;
      this.flight.angularVelocity.set(srand() * k, srand() * k, srand() * k);

      // Coin-flip: tail-rotor / drive-train failure → spins out of control.
      // Pick a direction once; the spin then ramps up each frame in update().
      this.spinDirection = Math.random() < d.failureSpinChance
        ? (Math.random() < 0.5 ? -1 : 1)
        : 0;

      if (this.particleSystem) {
        this.particleSystem.spawnExplosion(
          this.flight.position.x, this.flight.position.y, this.flight.position.z,
          { size: 1 });
      }
    }

    // 4. Runaway yaw spin — once spinDirection is set, accelerate toward the
    //    cap so the wreck progressively spins out of control as it falls.
    if (this.spinDirection) {
      const target = this.spinDirection * d.failureSpinMax;
      const step = d.failureSpinRamp * dt * this.spinDirection;
      const next = this.flight.angularVelocity.y + step;
      // Clamp toward target without overshooting in the chosen direction.
      this.flight.angularVelocity.y = this.spinDirection > 0
        ? Math.min(next, target)
        : Math.max(next, target);
    }
  }

  dispose() {
    // Geometries and materials are shared with the source asset (clone is
    // hierarchy-only); only detach from scene.
    this.group.parent?.remove(this.group);
  }
}
